import { Box, Flex, Grid, Text } from '@ttoss/ui';
import { Icon } from '@iconify/react';
import {
  LayerOption,
  LayerSymbol,
} from '../../providers/SiflorMapProvider/types';
import { useSiflorMap } from '../../providers/SiflorMapProvider/useSiflorMap';
import React from 'react';

type BiophysicsLegendsProps = {
  selectedLayer?: LayerOption;
};

export const BiophysicsLegends = ({
  selectedLayer,
}: BiophysicsLegendsProps) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const [layerSymbols, setLayerSymbols] = React.useState<LayerSymbol[]>([]);

  const { getLayerSymbols, isSiflorMapLoaded, getLayers } = useSiflorMap();

  const layers = React.useMemo(() => {
    if (isSiflorMapLoaded) {
      return getLayers();
    }

    return [];
  }, [isSiflorMapLoaded, getLayers]);

  const layerName = React.useMemo(() => {
    if (selectedLayer) {
      const layer = layers.find((l) => {
        return l.code === selectedLayer.groupCode;
      });

      if (layer) {
        const layerOption = layer.options.find((l) => {
          return l.code === selectedLayer.layerCode;
        });

        if (layerOption) {
          return layerOption.name;
        }
      }
    }

    return '';
  }, [layers, selectedLayer]);

  const legendTitle = ['Legenda', layerName].join(' - ');

  /**
   * This useEffect loads the symbols of the selected layer whenever selected
   * layer changes.
   */
  React.useEffect(() => {
    try {
      if (selectedLayer && isSiflorMapLoaded) {
        setLayerSymbols(getLayerSymbols(selectedLayer));
      } else {
        setLayerSymbols([]);
      }
    } catch (err) {
      setLayerSymbols([]);
    }
  }, [getLayerSymbols, selectedLayer, isSiflorMapLoaded]);

  const handleClick = () => {
    return setIsOpen((prev) => {
      return !prev;
    });
  };

  return (
    <Flex
      sx={{
        backgroundColor: 'background',
        width: '100%',
        paddingBottom: isOpen && layerSymbols.length > 0 ? 7 : null,
        boxShadow: 2,
        zIndex: 1,
        justifyContent: 'center',
      }}
    >
      {!selectedLayer || (selectedLayer && layerSymbols.length === 0) ? (
        <Flex
          sx={{
            alignItems: 'center',
            height: '34px',
            backgroundColor: 'secondary',
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <Text
            sx={{
              color: 'background',
              fontSize: 2,
            }}
          >
            {!selectedLayer
              ? 'Selecione uma camada para mais informações'
              : 'O camada selecionada não possui legendas'}
          </Text>
        </Flex>
      ) : (
        <Flex
          sx={{
            flexDirection: 'column',
            width: '100%',
            position: 'relative',
          }}
        >
          {isOpen && (
            <Flex
              sx={{ backgroundColor: 'secondary', justifyContent: 'center' }}
            >
              <Text
                sx={{
                  alignSelf: 'center',
                  color: 'background',
                  width: '21px',
                  position: 'absolute',
                  top: '-4px',
                }}
              >
                <Icon icon="akar-icons:minus" />
              </Text>
            </Flex>
          )}
          <Flex
            sx={{
              color: 'background',
              paddingLeft: 6,
              fontSize: 2,
              height: '34px',
              alignItems: 'center',
              backgroundColor: 'secondary',
            }}
            onClick={handleClick}
          >
            <Icon
              icon={
                !isOpen
                  ? 'ic:baseline-keyboard-arrow-up'
                  : 'ic:baseline-keyboard-arrow-down'
              }
            />

            <Text sx={{ color: 'background', fontSize: 2, marginLeft: 7 }}>
              {legendTitle}
            </Text>
          </Flex>

          {isOpen && layerSymbols.length > 0 && (
            <Grid
              sx={{
                height: '87px',
                overflowY: 'auto',
                paddingX: 6,
                rowGap: 5,
                columnGap: 7,
                marginTop: 6,
              }}
              columns={2}
            >
              {layerSymbols.map((legend) => {
                return (
                  <Flex
                    key={`legend-${legend.color}-${legend.label}`}
                    sx={{ gap: 4, alignItems: 'center' }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        minWidth: 10,
                        borderRadius: '50%',
                        backgroundColor: legend.color,
                      }}
                    />

                    <Text sx={{ fontSize: 1, color: 'neutral' }}>
                      {legend.label}
                    </Text>
                  </Flex>
                );
              })}
            </Grid>
          )}
        </Flex>
      )}
    </Flex>
  );
};
